import type { ReactNode } from 'react';
import { LuX } from 'react-icons/lu';
import { IconButton } from '../../design-system/components';

interface CelebrationPanelProps {
  onClose: () => void;
  /** Above the title (the intro's "Introduction complete" pill). */
  eyebrow?: ReactNode;
  title: ReactNode;
  /** Under the title (the throwback's "Course Complete"). */
  subtitle?: ReactNode;
  /** A small medallion on the avatar's corner (the throwback's trophy). */
  avatarBadge?: ReactNode;
  children: ReactNode;
  /** The panel's buttons, stacked under it. */
  actions: ReactNode;
}

/**
 * The text panels of the celebration modal (the intro's "On-Demand Empathy"
 * and the course's journey throwback) on the course card's skin: the warm
 * canvas with a peach glow behind Empy, the white surface, Space Grotesk
 * heading, the design-system close and buttons. The module and course cards
 * themselves are SVG (cardSkin.ts); this is their HTML sibling.
 *
 * Colours come from the redesign tokens, never DaisyUI's base-* theme
 * colours, so the panel matches the cards it follows whatever theme the page
 * runs (the app has one light theme; a dark DaisyUI theme would otherwise
 * flip half of the panel).
 */
export default function CelebrationPanel({
  onClose,
  eyebrow,
  title,
  subtitle,
  avatarBadge,
  children,
  actions,
}: CelebrationPanelProps) {
  return (
    <div className="w-full max-w-[95vw] sm:max-w-md md:max-w-[480px] flex flex-col gap-3 my-auto">
      <div
        data-celebration-panel=""
        className="relative rounded-[var(--ee-radius-lg)] overflow-hidden animate-scaleIn bg-[var(--ee-surface)] text-[var(--ee-navy)] font-[family-name:var(--ee-font-body)] shadow-[0_12px_30px_rgba(20,32,51,0.11),0_24px_60px_rgba(0,0,0,0.25)]"
      >
        <IconButton
          label="Close"
          icon={<LuX />}
          onClick={onClose}
          className="absolute top-2.5 right-2.5 z-20"
        />

        {/* The warm header: a peach glow behind Empy on the canvas */}
        <div className="relative pt-9 pb-6 px-6 sm:px-8 bg-[radial-gradient(ellipse_at_50%_0%,var(--ee-peach),var(--ee-canvas)_72%)]">
          <div className="relative w-20 h-20 mx-auto mb-4">
            <div className="w-full h-full rounded-full overflow-hidden bg-[var(--ee-gold-soft)] ring-4 ring-[var(--ee-surface)] shadow-[var(--ee-shadow-raised)]">
              <img
                src="/landing-giraffe.webp"
                alt="Empy"
                className="w-full h-full object-cover object-top"
              />
            </div>
            {avatarBadge}
          </div>

          {eyebrow ? <div className="flex justify-center mb-3">{eyebrow}</div> : null}

          <h2 className="font-[family-name:var(--ee-font-heading)] text-2xl font-bold text-center text-[var(--ee-navy)]">
            {title}
          </h2>
          {subtitle ? (
            <p className="mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-center text-[var(--ee-orange-text)]">
              {subtitle}
            </p>
          ) : null}
        </div>

        <div className="px-6 sm:px-8 pb-6 sm:pb-7 pt-5">{children}</div>
      </div>

      <div className="flex flex-col gap-2.5">{actions}</div>
    </div>
  );
}
